import { useEffect, useState } from "react";
import { ROUTE_FOCUS_EVENT } from "./focus-management";

export interface PortalLocationState {
  readonly pathname: string;
  readonly navigationEpoch: number;
}

interface PortalLocationEventTarget {
  addEventListener(type: string, listener: EventListener): void;
  removeEventListener(type: string, listener: EventListener): void;
}

interface PortalPathLocation {
  readonly pathname: string;
}

export function normalizePortalPathname(pathname: string) {
  const path = pathname.split(/[?#]/, 1)[0] || "/";
  if (!path.startsWith("/")) return "/";
  return path.length > 1 ? path.replace(/\/+$/, "") || "/" : path;
}

export function readPortalPathname(
  location: PortalPathLocation | undefined = typeof window === "undefined"
    ? undefined
    : window.location,
) {
  return normalizePortalPathname(location?.pathname ?? "/");
}

export function createPortalLocationState(
  pathname: string,
): PortalLocationState {
  return {
    pathname: normalizePortalPathname(pathname),
    navigationEpoch: 0,
  };
}

export function reducePortalLocation(
  state: PortalLocationState,
  pathname: string,
): PortalLocationState {
  const nextPathname = normalizePortalPathname(pathname);
  if (nextPathname === state.pathname) return state;

  return {
    pathname: nextPathname,
    navigationEpoch: state.navigationEpoch + 1,
  };
}

export function subscribeToPortalLocation(
  listener: EventListener,
  target: PortalLocationEventTarget = window,
) {
  target.addEventListener("popstate", listener);
  target.addEventListener(ROUTE_FOCUS_EVENT, listener);

  return () => {
    target.removeEventListener("popstate", listener);
    target.removeEventListener(ROUTE_FOCUS_EVENT, listener);
  };
}

export function usePortalLocation() {
  const [location, setLocation] = useState(() =>
    createPortalLocationState(readPortalPathname()),
  );

  useEffect(
    () =>
      subscribeToPortalLocation(() => {
        setLocation((current) =>
          reducePortalLocation(current, readPortalPathname()),
        );
      }),
    [],
  );

  return location;
}
